import imageCompression from 'browser-image-compression'
import { throttle } from 'lodash-es'
import type { InputHTMLAttributes } from 'react'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import { FileTrigger } from 'react-aria-components'
import Dropzone from 'react-dropzone'
import type { Area, CropperProps, Point } from 'react-easy-crop'
import Cropper from 'react-easy-crop'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import FluentHistory24Filled from '~icons/fluent/history-24-filled'
import IcBaselineZoomIn from '~icons/ic/baseline-zoom-in'
import IcBaselineZoomOut from '~icons/ic/baseline-zoom-out'
import UploadPictureIcon from '~icons/icon-park-outline/upload-picture'
import type { Media } from 'src/services/apollo/generated/graphql'
import { fetchGetMedia } from 'src/services/apollo/media'
import { cx } from 'src/utils/cx'
import {
  asyncScopedObjectUrl,
  useObjectUrl,
} from 'src/utils/hooks/useObjectUrl'
import { pickMedia, takePhoto } from 'src/utils/jsBridge/implementation'
import { downloadMedia } from 'src/utils/media'
import { isAndroidWebview } from 'src/utils/userAgent'
import { TaskMediaPicker } from '../GenerationTask/TaskMediaPicker'
import { UIButton } from '../ui/Button'
import {
  UIDialog,
  UIDialogContent,
  UIDialogFooter,
  UIDialogHeader,
} from '../ui/Modal'
import { UISlider } from '../ui/Slider'
import Text from '../ui/Text'
import getCroppedImg from './utils'

const DEFAULT_SIZE = 200

export const getFileSize = async (blob: Blob) => {
  const img = document.createElement('img')
  img.src = URL.createObjectURL(blob)
  img.style.display = 'none'
  document.body.appendChild(img)
  await new Promise<void>(resolve => {
    img.onload = () => {
      resolve()
    }
  })
  const size = {
    width: img.width,
    height: img.height,
  }
  document.body.removeChild(img)
  return size
}

export type CropImageRef = {
  getImage: () => Promise<File | undefined>
  zoom: number
  hasImage: boolean
  reset: () => void
}
type CropImageProps = {
  allowedFileTypes?: InputHTMLAttributes<HTMLInputElement>['accept']
  maxWidth?: number
  maxHeight?: number
  cropShape?: CropperProps['cropShape']
  onImageChange?: (hasImage: boolean) => void
}

const CropAndUploadImage = forwardRef<CropImageRef, CropImageProps>(
  function CropAndUploadImage(props, ref) {
    const {
      allowedFileTypes = 'image/png, image/jpeg',
      maxHeight = DEFAULT_SIZE,
      maxWidth = DEFAULT_SIZE,
      cropShape,
      onImageChange,
    } = props

    const { t } = useTranslation(['action'])
    const [zoom, setZoom] = useState(1)
    const [minZoom, setMinZoom] = useState(1)
    const [maxZoom, setMaxZoom] = useState(3)

    const [crop, setCrop] = useState<Point>({ x: 0, y: 0 })

    const [isGif, setIsGif] = useState(false)
    const [originalFile, setOriginalFile] = useState<File | null>(null)
    const originalImage = useObjectUrl(originalFile)
    const [resultFile, setResultFile] = useState<File | Blob | null>(null)

    const maxDim = Math.max(maxWidth, maxHeight)

    const [taskPickerOpen, setTaskPickerOpen] = useState(false)

    const onCropComplete = useCallback(
      throttle(async (_: Area | undefined, croppedAreaPixels: Area) => {
        if (!originalFile) return
        try {
          const cropped = isGif
            ? await import('./gifsicle-utils').then(({ cropGif }) =>
                cropGif(originalFile, croppedAreaPixels),
              )
            : await asyncScopedObjectUrl(originalFile, url =>
                getCroppedImg(url, croppedAreaPixels),
              )
          if (cropped) {
            setResultFile(cropped)
          }
        } catch (e) {
          toast.error(
            `Failed to crop image: ${e instanceof Error ? e.message : String(e)}`,
          )
        }
      }, 1000),
      [isGif, originalFile],
    )

    const onFileChanged = useCallback(
      async (file: File) => {
        const size = await getFileSize(file)
        // Ensure maxZoom > minZoom to prevent Slider NaN and Cropper infinite rerender
        const _maxZoom = Math.max(
          3,
          Number((Math.max(size.width, size.height) / maxDim).toFixed(2)),
        )
        setMinZoom(1)
        setMaxZoom(_maxZoom)
        setZoom(1)
        setCrop({ x: 0, y: 0 })

        setOriginalFile(file)
        setIsGif(file.type === 'image/gif')
        onImageChange?.(true)
      },
      [maxDim, onImageChange],
    )

    const resetImage = useCallback(() => {
      setOriginalFile(null)
      setResultFile(null)
      setZoom(1)
      onImageChange?.(false)
    }, [onImageChange])

    useImperativeHandle(ref, () => {
      return {
        getImage: async () => {
          let fileToUpload: File | Blob | null = null
          if (!isGif) {
            fileToUpload = await imageCompression(resultFile as File, {
              maxWidthOrHeight: maxDim,
              maxSizeMB: 1,
              useWebWorker: true,
              fileType: 'image/png',
            })
          } else {
            fileToUpload = resultFile
          }

          return fileToUpload as File
        },
        zoom,
        hasImage: !!originalFile,
        reset: resetImage,
      }
    }, [resultFile, isGif, maxDim, zoom, originalFile, resetImage])

    const accept = useMemo(() => {
      return Object.fromEntries(
        allowedFileTypes.split(',').map(s => [s.trim(), []]),
      )
    }, [allowedFileTypes])

    return (
      <div className="w-full">
        {!originalImage ? (
          <div className="p-8">
            <Dropzone
              onDrop={(files: File[]) => {
                if (files && files[0]) onFileChanged(files[0])
              }}
              noClick
              noKeyboard
              accept={accept}
            >
              {({ getRootProps, getInputProps }) => (
                <div
                  {...getRootProps()}
                  className="relative w-full min-h-[300px] mmd:min-h-[400px] rounded-lg border-2 border-dashed border-neutral-300 dark:border-neutral-600 bg-neutral-100/50 dark:bg-neutral-800/50 flex flex-col items-center justify-center gap-6"
                >
                  <div
                    className="text-center text-neutral-600 dark:text-neutral-300"
                    dangerouslySetInnerHTML={{
                      __html:
                        t('action:drag-and-drop-hint', { br: '<br />' }) ?? '',
                    }}
                  />

                  <div className="flex flex-col msm:flex-row gap-2 items-center justify-center">
                    <UIButton
                      className="w-full msm:w-auto msm:min-w-[200px]"
                      variant="outlined"
                      onPress={() => setTaskPickerOpen(true)}
                      icon={<FluentHistory24Filled className="size-5" />}
                    >
                      <Text i18nKey="action:image-source.from-task" />
                    </UIButton>

                    <FileTrigger
                      acceptedFileTypes={allowedFileTypes
                        .split(',')
                        .map(s => s.trim())}
                      onSelect={files => {
                        const file = files?.item(0)
                        if (file) onFileChanged(file)
                      }}
                    >
                      <UIButton
                        className="w-full msm:w-auto msm:min-w-[200px]"
                        variant="outlined"
                        icon={<UploadPictureIcon className="size-5" />}
                      >
                        <Text i18nKey="action:image-source.from-upload" />
                      </UIButton>
                    </FileTrigger>

                    {isAndroidWebview() && (
                      <>
                        <UIButton
                          variant="outlined"
                          onPress={async () => {
                            const data = await pickMedia({
                              mimeTypes: allowedFileTypes
                                .split(',')
                                .map(s => s.trim()),
                            })
                            if (data?.mediaId) {
                              const mediaRes = await fetchGetMedia({
                                id: data.mediaId,
                              })
                              const media = mediaRes.data?.media
                              if (media) {
                                const file = await downloadMedia(media)
                                if (file) onFileChanged(file)
                              }
                            }
                          }}
                        >
                          <Text i18nKey="action:image-source.from-native-photo-gallery" />
                        </UIButton>
                        <UIButton
                          variant="outlined"
                          onPress={async () => {
                            const data = await takePhoto()
                            if (data?.mediaId) {
                              const mediaRes = await fetchGetMedia({
                                id: data.mediaId,
                              })
                              const media = mediaRes.data?.media
                              if (media) {
                                const file = await downloadMedia(media)
                                if (file) onFileChanged(file)
                              }
                            }
                          }}
                        >
                          <Text i18nKey="action:image-source.from-native-camera" />
                        </UIButton>
                      </>
                    )}
                  </div>
                  <input {...getInputProps()} />
                </div>
              )}
            </Dropzone>
          </div>
        ) : (
          <div className="w-full p-4">
            <Dropzone
              onDrop={(files: File[]) => {
                if (files && files[0]) onFileChanged(files[0])
              }}
              noClick
              noKeyboard
              accept={accept}
            >
              {({ getRootProps, getInputProps }) => (
                <div
                  {...getRootProps()}
                  className="relative w-full rounded-lg overflow-hidden bg-neutral-500/20"
                  style={{
                    aspectRatio: maxWidth / maxHeight,
                    maxHeight: 'calc(var(--visible-height) - 280px)',
                  }}
                >
                  <Cropper
                    image={originalImage}
                    crop={crop}
                    zoom={zoom}
                    maxZoom={maxZoom}
                    minZoom={minZoom}
                    aspect={maxWidth / maxHeight}
                    showGrid={false}
                    onCropChange={setCrop}
                    onCropComplete={onCropComplete}
                    onZoomChange={val => {
                      setZoom(Number(val.toFixed(2)))
                    }}
                    cropShape={cropShape}
                  />
                  <input {...getInputProps()} />
                </div>
              )}
            </Dropzone>

            <div className="w-full flex justify-center mt-4">
              <div className="flex items-center gap-3 w-full max-w-md">
                <IcBaselineZoomOut className="size-5 shrink-0 text-neutral-600 dark:text-neutral-400" />
                <div className="flex-1 px-2">
                  <UISlider
                    className="w-full"
                    variant="half-filled"
                    step={0.01}
                    minValue={minZoom}
                    maxValue={maxZoom}
                    value={zoom}
                    onChange={val =>
                      setZoom(Number((val as number).toFixed(2)))
                    }
                    sliderOutputProps={{
                      className: 'hidden',
                    }}
                  />
                </div>
                <IcBaselineZoomIn className="size-5 shrink-0 text-neutral-600 dark:text-neutral-400" />
              </div>
            </div>
          </div>
        )}

        <TaskMediaPicker
          open={taskPickerOpen}
          onClose={() => setTaskPickerOpen(false)}
          onSubmit={async mediaId => {
            const res = await fetchGetMedia({ id: mediaId })
            const media = res.data?.media
            if (media) {
              const file = await downloadMedia(media)
              if (file) onFileChanged(file)
            }
          }}
        />
      </div>
    )
  },
)

function CropImageDialogContent(
  props: {
    file: File
    resolve: (blob: Blob | undefined) => void
    close: () => void
  } & Omit<CropImageProps, 'allowedFileTypes'>,
) {
  const { maxHeight = DEFAULT_SIZE, maxWidth = DEFAULT_SIZE, cropShape } = props

  const originalFile = props.file
  const [originalImage, setOriginalImage] = useState<string>()
  const [zoom, setZoom] = useState(1)
  const [minZoom, setMinZoom] = useState(1)
  const [maxZoom, setMaxZoom] = useState(3)
  const [fileReady, setFileReady] = useState(false)

  const [resultFile, setResultFile] = useState<File | Blob | null>(null)
  const resultImage = useObjectUrl(resultFile)

  const maxDim = Math.max(maxWidth, maxHeight)
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 })

  const onFileChanged = useCallback(
    async (file: File) => {
      setFileReady(false)
      const size = await getFileSize(file)
      // Ensure maxZoom > minZoom to prevent Slider NaN and Cropper infinite rerender
      const _maxZoom = Math.max(
        3,
        Number((Math.max(size.width, size.height) / maxDim).toFixed(2)),
      )
      setMinZoom(1)
      setMaxZoom(_maxZoom)
      setZoom(1)

      /**
       * Wait for motion animation to complete to
       * prevent miscalculated crop area
       *
       * @see https://github.com/ValentinH/react-easy-crop?tab=readme-ov-file#known-issues
       */
      setTimeout(() => {
        setFileReady(true)
      }, 300)
    },
    [maxDim],
  )

  useEffect(() => {
    onFileChanged(originalFile)

    // FIXME: useObjectUrl doesn't work for originalFile
    // Handling ObjectURL lifecycle manually
    const originalObjectUrl = URL.createObjectURL(originalFile)
    setOriginalImage(originalObjectUrl)

    return () => {
      URL.revokeObjectURL(originalObjectUrl)
    }
  }, [originalFile])

  const onCropComplete = useCallback(
    throttle(async (_: Area | undefined, croppedAreaPixels: Area) => {
      if (!originalFile) return
      try {
        const cropped = await asyncScopedObjectUrl(originalFile, url =>
          getCroppedImg(url, croppedAreaPixels),
        )
        if (cropped) {
          setResultFile(cropped)
        }
      } catch (e) {
        toast.error(
          `Failed to crop image: ${e instanceof Error ? e.message : String(e)}`,
        )
      }
    }, 1000),
    [originalFile],
  )

  const onConfirm = useCallback(() => {
    if (resultFile) {
      props.resolve(resultFile)
      props.close()
    } else {
      toast.error('Result not exists')
    }
  }, [resultFile, props])

  const onCancel = useCallback(() => {
    props.resolve(undefined)
    props.close()
  }, [props])

  return (
    <>
      <UIDialogHeader
        heading={<Text i18nKey="action:drag-and-move-hint" />}
      ></UIDialogHeader>
      <UIDialogContent>
        <div className="flex flex-wrap w-full gap-6">
          <div className="flex-1">
            <h3>
              <Text i18nKey="action:drag-and-move-hint" />
            </h3>
            <div
              className="relative mt-1"
              style={{
                width: maxWidth,
                height: maxHeight,
              }}
            >
              {fileReady && (
                <Cropper
                  image={originalImage}
                  crop={crop}
                  zoom={zoom}
                  maxZoom={maxZoom}
                  minZoom={minZoom}
                  aspect={maxWidth / maxHeight}
                  showGrid={false}
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                  onZoomChange={val => {
                    setZoom(Number(val.toFixed(2)))
                  }}
                  cropShape={cropShape}
                />
              )}
            </div>
          </div>
          <div className="flex-1 relative">
            <h3>
              <Text i18nKey="action:preview"></Text>
            </h3>
            <div
              className="flex items-center justify-center relative mt-1"
              style={{
                width: maxWidth,
                height: maxHeight,
              }}
            >
              {resultImage && (
                <img
                  width={maxWidth}
                  height={maxHeight}
                  src={resultImage}
                  className={cx(
                    'object-cover',
                    cropShape === 'round' && 'rounded-full',
                  )}
                />
              )}
            </div>
          </div>
        </div>
        <div className="w-full flex gap-2 items-center mt-4">
          <IcBaselineZoomOut className="size-6" />
          <UISlider
            variant="half-filled"
            className="flex-1"
            step={0.01}
            minValue={minZoom}
            maxValue={maxZoom}
            value={zoom}
            onChange={val => setZoom(Number((val as number).toFixed(2)))}
          />
          <IcBaselineZoomIn className="size-6" />
        </div>
      </UIDialogContent>
      <UIDialogFooter>
        <UIButton onPress={onCancel} variant="outlined">
          <Text i18nKey="action:crop.skip-crop.label"></Text>
        </UIButton>
        <UIButton onPress={onConfirm} variant="filled">
          <Text i18nKey="action:confirm"></Text>
        </UIButton>
      </UIDialogFooter>
    </>
  )
}

/**
 * Accept an uncropped image, returns a cropped one wrapped in a Promise
 * Doesn't contain uploading logic
 *
 * @param args
 * @returns
 */
export async function dialogCropImage(args: { file: File }) {
  return UIDialog.create<Blob | undefined>({
    dismissable: true,
    children: ({ resolve, closeDialog }) => (
      <CropImageDialogContent
        file={args.file}
        resolve={resolve}
        close={closeDialog}
      />
    ),
    className: 'max-w-screen-mlg',
  })
}

export function CropCurrentImageForUploadButton(
  props: StyleProps & { media?: Media; action: (file: File) => void },
) {
  if (!props.media) return null
  return (
    <UIButton
      variant="filled"
      busy="auto"
      onPress={async () => {
        const file = await downloadMedia(props.media)
        if (!file) return
        const parsed = await dialogCropImage({ file })
        if (!parsed) return
        props.action(parsed as File)
      }}
    >
      <Text i18nKey={'action:crop.adjust-crop.label'} />
    </UIButton>
  )
}

export default CropAndUploadImage
